/**
 * Canal da venda: uma regra só para o Hoje, o Funil e a tela Vendas (dono, 29/10/2026). É o canal que
 * o servidor resolve no pedido (`painelSales.ts`, `salesChannel`), não a `utm_source` crua:
 *
 * - **Anúncio**: `meta_ads` (fonte da Meta, `utm_content` com o ID do anúncio ou meio pago; o link com
 *   `{{site_source_name}}` manda `utm_source=ig/fb` e ainda assim é anúncio);
 * - **Orgânico**: tudo o que tem origem e não é anúncio (bio, direct, automação, WhatsApp, caixinha);
 * - **Sem origem**: o pedido chegou sem UTM nenhuma. Nunca é presumido orgânico nem pago.
 *
 * O Hoje pede as vendas de cada canal com o filtro de origem que o provider já entende: o canal vai
 * como um marcador (`CHANNEL_MARK`) junto das origens escolhidas nos Filtros, e o provider ao vivo
 * aplica as duas coisas (canal E origem).
 */

export type ChannelGroup = 'ads' | 'org' | 'none';

/** Canal do servidor (`meta_ads`, `ig_bio`, `automacao`, `outro`, `sem_origem`...) → o grupo da tela. */
export function channelGroup(channel: string | null | undefined): ChannelGroup {
  if (channel === 'meta_ads') return 'ads';
  if (!channel || channel === 'sem_origem') return 'none';
  return 'org';
}

/** Marcadores de canal no filtro de origem (nenhuma `utm_source` real começa com "@"). */
export const CHANNEL_MARK: Readonly<Record<Exclude<ChannelGroup, 'none'>, string>> = {
  ads: '@canal:anuncio',
  org: '@canal:organico',
};

const MARK_GROUP: ReadonlyMap<string, ChannelGroup> = new Map([
  [CHANNEL_MARK.ads, 'ads'],
  [CHANNEL_MARK.org, 'org'],
]);

export const isChannelMark = (s: string): boolean => MARK_GROUP.has(s);

/**
 * Separa o filtro de origem em canais (os marcadores) e origens de verdade (as dos Filtros). Sem
 * marcador, `groups` é null (qualquer canal).
 */
export function splitOriginFilter(utmSources: readonly string[] | undefined): {
  groups: ReadonlySet<ChannelGroup> | null;
  sources: string[];
} {
  const groups = new Set<ChannelGroup>();
  const sources: string[] = [];
  for (const s of utmSources ?? []) {
    const g = MARK_GROUP.get(s);
    if (g) groups.add(g);
    else sources.push(s);
  }
  return { groups: groups.size > 0 ? groups : null, sources };
}
